import { ActionContext, ActionTree } from 'vuex';
import LagerService from '@/modules/ewerp/service/LagerService';

import { RootState } from '@/modules/ewerp/store';
import { useEwerpCommonStore } from '@/modules/ewerp/store/ewerpCommonStore';

import { State } from './state';

import { Mutations, MutationTypes } from './mutations';

import {
  Hersteller,
  Herstellerartikel,
  Lieferant,
  Lieferantartikel,
  Lieferherstartikel,
  Materialartikel,
  Materiallieferartikel,
} from '@/modules/ewerp/models/LagerMaterialModels';

export enum ActionTypes {
  fetchMaterialartikel = 'FETCHMATERIALARTIKEL',
  fetchMateriallieferantartikel = 'FETCHMATERIALLIEFERANTARTIKEL',
  fetchLieferantartikel = 'FETCHLIEFERANTARTIKEL',
  fetchLieferanten = 'FETCHLIEFERANT',
  fetchLieferherstartikel = 'FETCHLIEFERHERSTARTIKEL',
  fetchHerstellerartikel = 'FETCHHERSTELLERARTIKEL',
  fetchHersteller = 'FETCHHERSTELLER',
  syncMdmMaterialartikelEtiketten = 'SYNCMATERIALARTIKELETIKETTEN',
  syncMdmMaterialartikelKartons = 'SYNCMATERIALARTIKELKARTONS',
  syncMdmMaterialartikelFlaschen = 'SYNCMATERIALARTIKELFLASCHEN',
}

type ActionArguments = Omit<ActionContext<State, RootState>, 'commit'> & {
  commit<K extends keyof Mutations>(key: K, payload: Parameters<Mutations[K]>[1]): ReturnType<Mutations[K]>;
};

export type Actions = {
  [ActionTypes.fetchMaterialartikel](context: ActionArguments): Promise<void>;
  [ActionTypes.fetchMateriallieferantartikel](context: ActionArguments): Promise<void>;
  [ActionTypes.fetchLieferantartikel](context: ActionArguments): Promise<void>;
  [ActionTypes.fetchLieferanten](context: ActionArguments): Promise<void>;
  [ActionTypes.fetchLieferherstartikel](context: ActionArguments): Promise<void>;
  [ActionTypes.fetchHerstellerartikel](context: ActionArguments): Promise<void>;
  [ActionTypes.fetchHersteller](context: ActionArguments): Promise<void>;
  [ActionTypes.syncMdmMaterialartikelEtiketten](context: ActionArguments): Promise<void>;
  [ActionTypes.syncMdmMaterialartikelKartons](context: ActionArguments): Promise<void>;
  [ActionTypes.syncMdmMaterialartikelFlaschen](context: ActionArguments): Promise<void>;
};

export const actions: ActionTree<State, RootState> & Actions = {
  async [ActionTypes.fetchMaterialartikel]({ commit }: ActionArguments) {
    const commonStore = useEwerpCommonStore();
    const status = commonStore.status;
    status.materialartikelLoading = true;
    commonStore.setStatus(status);

    return LagerService.getMaterialartikel()
      .then((res) => {
        status.materialartikelLoading = false;
        commonStore.setStatus(status);

        const result: Array<Materialartikel> = res.data;
        commit(MutationTypes.getMaterialartikelSuccess, result);
      })
      .catch((error) => {
        status.materialartikelLoading = false;
        // console.log(error);
        if (error.message === 'Network Error') {
          status.materialartikelErrorMsg = error.message;
        } else if (error.response?.status == 401) {
          status.materialartikelErrorMsg = 'Sie haben keinen Zugriff auf diese Daten';
        } else {
          status.materialartikelErrorMsg = error;
        }

        commonStore.setStatus(status);
      });
  },

  async [ActionTypes.fetchMateriallieferantartikel]({ commit }: ActionArguments) {
    const commonStore = useEwerpCommonStore();
    const status = commonStore.status;
    status.materiallieferantartikelLoading = true;
    commonStore.setStatus(status);

    return LagerService.getMateriallieferantartikel()
      .then((res) => {
        status.materiallieferantartikelLoading = false;
        commonStore.setStatus(status);

        const result: Array<Materiallieferartikel> = res.data;
        commit(MutationTypes.getMateriallieferantartikelSuccess, result);
      })
      .catch((error) => {
        status.materiallieferantartikelLoading = false;
        // console.log(error);
        if (error.message === 'Network Error') {
          status.materiallieferantartikelErrorMsg = error.message;
        } else if (error.response?.status == 401) {
          status.materiallieferantartikelErrorMsg = 'Sie haben keinen Zugriff auf diese Daten';
        } else {
          status.materiallieferantartikelErrorMsg = error;
        }

        sessionStorage.removeItem('materiallieferantartikel');
        commonStore.setStatus(status);
      });
  },

  async [ActionTypes.fetchLieferantartikel]({ commit }: ActionArguments) {
    const commonStore = useEwerpCommonStore();
    const status = commonStore.status;
    status.lieferantartikelLoading = true;
    commonStore.setStatus(status);

    return LagerService.getLieferantartikel()
      .then((res) => {
        status.lieferantartikelLoading = false;
        commonStore.setStatus(status);

        const result: Array<Lieferantartikel> = res.data;
        commit(MutationTypes.getLieferantartikelSuccess, result);
      })
      .catch((error) => {
        status.lieferantartikelLoading = false;
        // console.log(error);
        if (error.message === 'Network Error') {
          status.lieferantartikelErrorMsg = error.message;
        } else if (error.response?.status == 401) {
          status.lieferantartikelErrorMsg = 'Sie haben keinen Zugriff auf diese Daten';
        } else {
          status.lieferantartikelErrorMsg = error;
        }

        sessionStorage.removeItem('lieferantartikel');
        commonStore.setStatus(status);
      });
  },

  async [ActionTypes.fetchLieferanten]({ commit }: ActionArguments) {
    const commonStore = useEwerpCommonStore();
    const status = commonStore.status;
    status.lieferantLoading = true;
    commonStore.setStatus(status);

    return LagerService.getLieferanten()
      .then((res) => {
        status.lieferantLoading = false;
        commonStore.setStatus(status);

        const result: Array<Lieferant> = res.data;
        commit(MutationTypes.getLieferantenSuccess, result);
      })
      .catch((error) => {
        status.lieferantLoading = false;
        // console.log(error);
        if (error.message === 'Network Error') {
          status.lieferantErrorMsg = error.message;
        } else if (error.response?.status == 401) {
          status.lieferantErrorMsg = 'Sie haben keinen Zugriff auf diese Daten';
        } else {
          status.lieferantErrorMsg = error;
        }

        sessionStorage.removeItem('lieferanten');
        commonStore.setStatus(status);
      });
  },

  async [ActionTypes.fetchLieferherstartikel]({ commit }: ActionArguments) {
    const commonStore = useEwerpCommonStore();
    const status = commonStore.status;
    status.lieferherstartikelLoading = true;
    commonStore.setStatus(status);

    return LagerService.getLieferherstartikel()
      .then((res) => {
        status.lieferherstartikelLoading = false;
        commonStore.setStatus(status);

        const result: Array<Lieferherstartikel> = res.data;
        commit(MutationTypes.getLieferherstartikelSuccess, result);
      })
      .catch((error) => {
        status.lieferherstartikelLoading = false;
        // console.log(error);
        if (error.message === 'Network Error') {
          status.lieferherstartikelErrorMsg = error.message;
        } else if (error.response?.status == 401) {
          status.lieferherstartikelErrorMsg = 'Sie haben keinen Zugriff auf diese Daten';
        } else {
          status.lieferherstartikelErrorMsg = error;
        }

        sessionStorage.removeItem('lieferherstartikel');
        commonStore.setStatus(status);
      });
  },

  async [ActionTypes.fetchHerstellerartikel]({ commit }: ActionArguments) {
    const commonStore = useEwerpCommonStore();
    const status = commonStore.status;
    status.herstellerartikelLoading = true;
    commonStore.setStatus(status);

    return LagerService.getHerstellerartikel()
      .then((res) => {
        status.herstellerartikelLoading = false;
        commonStore.setStatus(status);

        const result: Array<Herstellerartikel> = res.data;
        commit(MutationTypes.getHerstellerartikelSuccess, result);
      })
      .catch((error) => {
        status.herstellerartikelLoading = false;
        // console.log(error);
        if (error.message === 'Network Error') {
          status.herstellerartikelErrorMsg = error.message;
        } else if (error.response?.status == 401) {
          status.herstellerartikelErrorMsg = 'Sie haben keinen Zugriff auf diese Daten';
        } else {
          status.herstellerartikelErrorMsg = error;
        }

        sessionStorage.removeItem('herstellerartikel');
        commonStore.setStatus(status);
      });
  },

  async [ActionTypes.fetchHersteller]({ commit }: ActionArguments) {
    const commonStore = useEwerpCommonStore();
    const status = commonStore.status;
    status.herstellerLoading = true;
    commonStore.setStatus(status);

    return LagerService.getHersteller()
      .then((res) => {
        status.herstellerLoading = false;
        commonStore.setStatus(status);

        const result: Array<Hersteller> = res.data;
        commit(MutationTypes.getHerstellerSuccess, result);
      })
      .catch((error) => {
        status.herstellerLoading = false;
        // console.log(error);
        if (error.message === 'Network Error') {
          status.herstellerErrorMsg = error.message;
        } else if (error.response?.status == 401) {
          status.herstellerErrorMsg = 'Sie haben keinen Zugriff auf diese Daten';
        } else {
          status.herstellerErrorMsg = error;
        }

        sessionStorage.removeItem('hersteller');
        commonStore.setStatus(status);
      });
  },

  //#region MDM
  async [ActionTypes.syncMdmMaterialartikelEtiketten]({}: ActionArguments) {
    const commonStore = useEwerpCommonStore();
    const status = commonStore.status;
    status.mdmSyncMaterialartikelLoading = true;
    status.mdmSyncMaterialartikelErrorMsg = '';
    commonStore.setStatus(status);

    return LagerService.syncMdmMaterialartikelEtiketten()
      .then((_res) => {})
      .catch((error) => {
        if (error.message === 'Network Error') {
          status.mdmSyncMaterialartikelErrorMsg = error.message;
        } else if (error.response?.status == 401) {
          status.mdmSyncMaterialartikelErrorMsg = 'Sie haben keinen Zugriff auf diese Daten';
        } else {
          status.mdmSyncMaterialartikelErrorMsg = error;
        }
      })
      .finally(() => {
        status.mdmSyncMaterialartikelLoading = false;

        commonStore.setStatus(status);
      });
  },

  async [ActionTypes.syncMdmMaterialartikelKartons]({}: ActionArguments) {
    const commonStore = useEwerpCommonStore();
    const status = commonStore.status;
    status.mdmSyncMaterialartikelLoading = true;
    status.mdmSyncMaterialartikelErrorMsg = '';
    commonStore.setStatus(status);

    return LagerService.syncMdmMaterialartikelKartons()
      .then((_res) => {})
      .catch((error) => {
        if (error.message === 'Network Error') {
          status.mdmSyncMaterialartikelErrorMsg = error.message;
        } else if (error.response?.status == 401) {
          status.mdmSyncMaterialartikelErrorMsg = 'Sie haben keinen Zugriff auf diese Daten';
        } else {
          status.mdmSyncMaterialartikelErrorMsg = error;
        }
      })
      .finally(() => {
        status.mdmSyncMaterialartikelLoading = false;

        commonStore.setStatus(status);
      });
  },

  async [ActionTypes.syncMdmMaterialartikelFlaschen]({}: ActionArguments) {
    const commonStore = useEwerpCommonStore();
    const status = commonStore.status;
    status.mdmSyncMaterialartikelLoading = true;
    status.mdmSyncMaterialartikelErrorMsg = '';
    commonStore.setStatus(status);

    return LagerService.syncMdmMaterialartikelFlaschen()
      .then((_res) => {})
      .catch((error) => {
        if (error.message === 'Network Error') {
          status.mdmSyncMaterialartikelErrorMsg = error.message;
        } else if (error.response?.status == 401) {
          status.mdmSyncMaterialartikelErrorMsg = 'Sie haben keinen Zugriff auf diese Daten';
        } else {
          status.mdmSyncMaterialartikelErrorMsg = error;
        }
      })
      .finally(() => {
        status.mdmSyncMaterialartikelLoading = false;

        commonStore.setStatus(status);
      });
  },
  //#endregion
};
